import React, { useMemo, useRef } from 'react'
import PropTypes from 'prop-types'
import ReactApexChart from 'react-apexcharts'
import useContainerWidth from '../../hooks/useContainerWidth'
import { useChartZoomSampling } from '../Progression/hooks'
import {
  AXIS_LABEL_STYLE, AXIS_TITLE_STYLE, DS_COLORS, bucketShape,
  buildChartBaseOptions, withCategories
} from '../charts/config'

const buildOptions = ({ mode, gradeMax, chartId }) => {
  const isPercent = mode === 'satisfaction'
  return {
    ...buildChartBaseOptions('area', chartId),
    colors: [DS_COLORS.info],
    stroke: { curve: 'smooth', width: 2 },
    fill: { type: 'gradient', gradient: { opacityFrom: 0.45, opacityTo: 0.1 } },
    dataLabels: { enabled: false },
    yaxis: {
      max: isPercent ? 100 : gradeMax,
      min: 0,
      tickAmount: 4,
      title: {
        text: isPercent ? 'Taux de Satisfaction (%)' : 'Note moyenne',
        style: AXIS_TITLE_STYLE
      },
      labels: {
        formatter: value => isPercent ? `${Math.round(value)} %` : value.toLocaleString('fr-FR'),
        style: AXIS_LABEL_STYLE
      }
    }
  }
}

const seriesData = (buckets, mode) => {
  const isPercent = mode === 'satisfaction'
  return buckets.map(bucket => {
    if (bucket.value === null || typeof bucket.value !== 'number')
      return null
    return isPercent ? Math.round(bucket.value) : bucket.value
  })
}

const EXTREMITY_LABELS_CLASSES = 'tw:flex tw:justify-between tw:px-2 tw:mt-1 '
  + 'tw:text-sm tw:font-medium tw:text-ds-neutral-disabled'

const EvolutionAreaChart = ({ buckets, gradeMax, mode, hideToolbar, compactLabels, chartId }) => {
  const containerRef = useRef(null)
  const containerWidth = useContainerWidth(containerRef)
  const handleZoomed = useChartZoomSampling(buckets, containerWidth)

  const options = useMemo(() => {
    const base = buildOptions({ mode, gradeMax, chartId })
    const opts = withCategories(base, buckets, { containerWidth })
    const chartConfig = { ...opts.chart, parentHeightOffset: 0, events: { zoomed: handleZoomed } }
    if (hideToolbar)
      chartConfig.toolbar = { show: false }
    const compactXaxis = { ...opts.xaxis, labels: { ...opts.xaxis.labels, show: false } }
    const xaxis = compactLabels ? compactXaxis : opts.xaxis
    return { ...opts, chart: chartConfig, xaxis }
  }, [buckets, containerWidth, handleZoomed, mode, gradeMax, hideToolbar, compactLabels, chartId])

  const series = useMemo(() => [{
    name: mode === 'satisfaction' ? 'Taux de Satisfaction' : 'Note moyenne',
    data: seriesData(buckets, mode)
  }], [buckets, mode])

  const showExtremities = compactLabels && buckets.length > 0
  const lastBucket = buckets[buckets.length - 1]

  return (
    <div ref={containerRef}>
      <ReactApexChart height={320} options={options} series={series} type="area" />
      {showExtremities ? (
        <div className={EXTREMITY_LABELS_CLASSES}>
          <span>{buckets[0].label}</span>
          <span>{lastBucket.label}</span>
        </div>
      ) : null}
    </div>
  )
}

EvolutionAreaChart.propTypes = {
  buckets: PropTypes.arrayOf(bucketShape).isRequired,
  chartId: PropTypes.string,
  compactLabels: PropTypes.bool,
  gradeMax: PropTypes.number.isRequired,
  hideToolbar: PropTypes.bool,
  mode: PropTypes.oneOf(['grade', 'satisfaction']).isRequired
}

EvolutionAreaChart.defaultProps = {
  chartId: null,
  compactLabels: false,
  hideToolbar: false
}

export default EvolutionAreaChart
